import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateCuring } from "./curing";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import * as geojson from "geojson";

import { api } from "../utils/api";
import { ApiError, ApiResponse } from "../types/api";
import { StrategyContext } from "../contexts/StrategyContext";

const bumpVegetationTileVersion = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string | undefined
) => {
  if (!projectId) return;
  queryClient.setQueryData<number>(
    ["vegetation-tile-version", projectId],
    () => Date.now()
  );
};

export interface ApplyVegetationEditPayload {
  vegClass: string;
  geojson: geojson.FeatureCollection;
  width?: number;
}

export interface ApplyVegetationEditResult {
  affectedCount: number;
  insertedCount: number;
}

export const useApplyVegetationEdit = () => {
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  const { refreshStrategy } = useContext(StrategyContext);

  return useMutation<ApiResponse<ApplyVegetationEditResult>, ApiError, ApplyVegetationEditPayload>({
    mutationFn: async (payload: ApplyVegetationEditPayload) => {
      return api.request<ApiResponse<ApplyVegetationEditResult>>(
        `/api/projects/${projectId}/vegetations/apply_edit`,
        {
          method: "POST",
          body: {
            veg_class: payload.vegClass,
            width: payload.width,
            geojson: payload.geojson,
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vegetationSources"] });
      invalidateCuring(queryClient, projectId);
      queryClient.invalidateQueries({ queryKey: ["strategy"] });
      queryClient.invalidateQueries({ queryKey: ["strategyJob"] });
      queryClient.invalidateQueries({ queryKey: ["strategyStatus"] });
      bumpVegetationTileVersion(queryClient, projectId);
      refreshStrategy();
    },
  });
};
